import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import moment from 'moment';

/**
 * Column definitions for the manual XP award audit grid.
 *
 * Fields map to a row from GET /student-portal/skillx/xp/manual-awards, which
 * joins the names an auditor actually reads onto the award — learner, mentor,
 * batch, rule label — rather than leaving raw ids.
 *
 * Remarks get real width: they are the reason the audit exists, and a truncated
 * "Great work on th…" answers nothing. A revoked row shows why it was revoked
 * in the same column, since that is the follow-up question every time.
 */
export const SkillxXpAwards_Table_Head = [
  {
    field: 'awarded_at',
    headerName: 'Awarded',
    width: 145,
    renderCell: (params) => (
      <Stack>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {params.row.awarded_at ? moment(params.row.awarded_at).format('DD MMM YYYY') : '—'}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: '#66708A' }}>
          {params.row.awarded_at ? moment(params.row.awarded_at).format('hh:mm A') : ''}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'student_name',
    headerName: 'Learner',
    width: 200,
    renderCell: (params) => (
      <Stack>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>
          {params.row.student_name || `Learner ${params.row.admn_no}`}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.4}>
          <Icon icon="solar:user-id-linear" width={12} height={12} color="#66708A" />
          <Typography sx={{ fontSize: 11.5, color: '#66708A' }}>{params.row.admn_no}</Typography>
        </Stack>
      </Stack>
    ),
  },
  {
    field: 'batch_name',
    headerName: 'Batch',
    width: 170,
    renderCell: (params) => (
      <Typography noWrap sx={{ fontSize: 12.5 }}>
        {params.row.batch_name || `Batch ${params.row.batch_id}`}
      </Typography>
    ),
  },
  {
    field: 'rule_label',
    headerName: 'Reason',
    width: 190,
    renderCell: (params) => (
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Icon
          icon={params.row.rule_icon || 'solar:star-shine-bold-duotone'}
          width={16}
          height={16}
          color="#7C4E9F"
        />
        <Typography noWrap sx={{ fontSize: 12.5 }}>
          {params.row.rule_label || params.row.rule_key}
        </Typography>
      </Stack>
    ),
  },
  {
    field: 'points',
    headerName: 'XP',
    width: 95,
    renderCell: (params) => {
      const revoked = params.row.status === 'REVOKED';
      return (
        <Stack direction="row" alignItems="center" spacing={0.4}>
          <Icon
            icon="solar:bolt-bold-duotone"
            width={15}
            height={15}
            color={revoked ? '#8E9AAF' : '#2E75BC'}
          />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 800,
              color: revoked ? '#8E9AAF' : '#1B2438',
              textDecoration: revoked ? 'line-through' : 'none',
            }}
          >
            {params.row.points}
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: 'remarks',
    headerName: 'Remarks',
    width: 300,
    renderCell: (params) => (
      <Tooltip arrow title={params.row.remarks || ''}>
        <Typography noWrap sx={{ fontSize: 12.5, color: '#1B2438' }}>
          {params.row.remarks || '—'}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'awarded_by_name',
    headerName: 'Awarded By',
    width: 165,
    renderCell: (params) => (
      <Typography noWrap sx={{ fontSize: 12.5 }}>
        {params.row.awarded_by_name || `User ${params.row.awarded_by}`}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 135,
    renderCell: (params) => {
      if (params.row.status !== 'REVOKED') {
        return (
          <Chip
            size="small"
            label="Active"
            sx={{ backgroundColor: '#c8facd', color: '#0db05d', fontWeight: 600 }}
          />
        );
      }
      return (
        <Tooltip
          arrow
          title={`Revoked by ${params.row.revoked_by_name || params.row.revoked_by} on ${
            params.row.revoked_at ? moment(params.row.revoked_at).format('DD MMM YYYY') : '—'
          } — ${params.row.revoke_reason || 'no reason recorded'}`}
        >
          <Chip
            size="small"
            label="Revoked"
            sx={{ backgroundColor: '#ffe7e7', color: '#c0392b', fontWeight: 600 }}
          />
        </Tooltip>
      );
    },
  },
];
